/**
 * Directives feature — client-side API surface.
 *
 * Every endpoint here POSTs to keyon's tswebhook.php and is live. The mock
 * helpers (`searchDirectivesMock()`, `getDirectiveCatalogMock()`,
 * `getDirectiveRecordsMock()`, `getDirectivePatientsMock()`) are retained only
 * as clearly-labeled fallbacks for local work when keyon is unavailable.
 *
 * The data contract here mirrors the MySQL migration exactly:
 *   db/migrations/mysql/20260621000000_directives_schema.sql
 *   - directive_record  (aggregate: immutable content + mutable lifecycle)
 *   - directive         (write-once coded statement)
 *   - directive_event   (append-only audit trail, snapshotted actor)
 *   - directive_catalog (curated common directives, seeded)
 *
 */

import axios from "axios";
import { CURR_API } from "@/constants/apis";

const WEBHOOK = `${CURR_API}/tswebhook.php`;

// ---------------------------------------------------------------------------
// Enum mirrors (kept in sync with the migration ENUM definitions). Exported so
// the UI never hand-writes a magic string that could drift from the schema.
// ---------------------------------------------------------------------------

/** directive_record.status */
export const DIRECTIVE_STATUS = Object.freeze({
  UNCONFIRMED_AUTHORIZATION: "unconfirmed_authorization",
  PENDING_REVIEW: "pending_review",
  ACTIVE: "active",
  SUPERSEDED: "superseded",
  REVOKED: "revoked",
  EXPIRED: "expired",
});

/** directive_record.ingest_path */
export const INGEST_PATH = Object.freeze({
  UPLOAD: "upload",
  MANUAL: "manual",
});

/** directive_record.relationship_to_patient */
export const RELATIONSHIP_TO_PATIENT = Object.freeze({
  SELF: "self",
  SPOUSE: "spouse",
  DOMESTIC_PARTNER: "domestic_partner",
  PARENT: "parent",
  CHILD: "child",
  SIBLING: "sibling",
  CAREGIVER: "caregiver",
  HEALTH_CARE_PROXY: "health_care_proxy",
  FRIEND: "friend",
  OTHER_RELATIVE: "other_relative",
  OTHER: "other",
});

/** directive_record.authority_type (only when is_legal_surrogate = 1) */
export const AUTHORITY_TYPE = Object.freeze({
  LEGAL_GUARDIAN: "legal_guardian",
  HEALTH_CARE_PROXY: "health_care_proxy",
  POWER_OF_ATTORNEY: "power_of_attorney",
});

/** directive_event.event_type */
export const EVENT_TYPE = Object.freeze({
  CREATED: "created",
  AUTHORIZATION_CONFIRMED: "authorization_confirmed",
  SIGNED: "signed",
  REVIEWED: "reviewed",
  REVIEW_NEEDED: "review_needed",
  REVOKED: "revoked",
  SUPERSEDED: "superseded",
  REAUTHORIZED: "reauthorized",
  DIRECTIVES_ADDED: "directives_added",
});

/** directive.provenance / directive.candidate_status */
export const PROVENANCE = Object.freeze({
  MANUAL: "manual",
  SIGNOFF: "signoff",
  EXTRACTED: "extracted",
  // Assigned by a reviewer (privilege 53) on an uploaded record before sign-off,
  // as opposed to SIGNOFF — assigned by the signer as part of activation.
  REVIEW: "review",
});
export const CANDIDATE_STATUS = Object.freeze({
  CONFIRMED: "confirmed",
  PENDING_CONFIRMATION: "pending_confirmation",
});

// ---------------------------------------------------------------------------
// Display helpers (label maps for the enums above).
// ---------------------------------------------------------------------------

export const STATUS_LABELS = Object.freeze({
  [DIRECTIVE_STATUS.ACTIVE]: "Currently Active",
  [DIRECTIVE_STATUS.PENDING_REVIEW]: "Pending Review",
  [DIRECTIVE_STATUS.UNCONFIRMED_AUTHORIZATION]: "Unconfirmed Authorization",
  [DIRECTIVE_STATUS.EXPIRED]: "Expired",
  [DIRECTIVE_STATUS.SUPERSEDED]: "Superseded",
  [DIRECTIVE_STATUS.REVOKED]: "Revoked",
});

/**
 * Status dot colors for the badge/legend leading dot. Kept in the existing
 * directives badge palette (Chakra-ish 500 weights) so the dot reads
 * consistently with `dir-badge` classes.
 */
export const STATUS_DOTS = Object.freeze({
  [DIRECTIVE_STATUS.ACTIVE]: "#38a169",
  [DIRECTIVE_STATUS.PENDING_REVIEW]: "#3182ce",
  [DIRECTIVE_STATUS.UNCONFIRMED_AUTHORIZATION]: "#dd6b20",
  [DIRECTIVE_STATUS.EXPIRED]: "#718096",
  [DIRECTIVE_STATUS.SUPERSEDED]: "#805ad5",
  [DIRECTIVE_STATUS.REVOKED]: "#e53e3e",
});

/** One-line status descriptions for the status legend. */
export const STATUS_DESCRIPTIONS = Object.freeze({
  [DIRECTIVE_STATUS.ACTIVE]: "In effect now — at most one per patient.",
  [DIRECTIVE_STATUS.PENDING_REVIEW]: "Authorized; awaiting clinician signature.",
  [DIRECTIVE_STATUS.UNCONFIRMED_AUTHORIZATION]: "Uploaded but not yet notarized.",
  [DIRECTIVE_STATUS.EXPIRED]: "Past its validity window.",
  [DIRECTIVE_STATUS.SUPERSEDED]: "Replaced by a newer record.",
  [DIRECTIVE_STATUS.REVOKED]: "Withdrawn; no longer valid.",
});

export const RELATIONSHIP_LABELS = Object.freeze({
  [RELATIONSHIP_TO_PATIENT.SELF]: "Self (the patient)",
  [RELATIONSHIP_TO_PATIENT.SPOUSE]: "Spouse",
  [RELATIONSHIP_TO_PATIENT.DOMESTIC_PARTNER]: "Domestic Partner",
  [RELATIONSHIP_TO_PATIENT.PARENT]: "Parent",
  [RELATIONSHIP_TO_PATIENT.CHILD]: "Child",
  [RELATIONSHIP_TO_PATIENT.SIBLING]: "Sibling",
  [RELATIONSHIP_TO_PATIENT.CAREGIVER]: "Caregiver",
  [RELATIONSHIP_TO_PATIENT.HEALTH_CARE_PROXY]: "Health Care Proxy",
  [RELATIONSHIP_TO_PATIENT.FRIEND]: "Friend",
  [RELATIONSHIP_TO_PATIENT.OTHER_RELATIVE]: "Other Relative",
  [RELATIONSHIP_TO_PATIENT.OTHER]: "Other",
});

export const AUTHORITY_LABELS = Object.freeze({
  [AUTHORITY_TYPE.LEGAL_GUARDIAN]: "Legal Guardian",
  [AUTHORITY_TYPE.HEALTH_CARE_PROXY]: "Health Care Proxy",
  [AUTHORITY_TYPE.POWER_OF_ATTORNEY]: "Power of Attorney",
});

export const EVENT_LABELS = Object.freeze({
  [EVENT_TYPE.CREATED]: "Created",
  [EVENT_TYPE.AUTHORIZATION_CONFIRMED]: "Authorization confirmed",
  [EVENT_TYPE.SIGNED]: "Signed (clinician sign-off)",
  [EVENT_TYPE.REVIEWED]: "Reviewed",
  [EVENT_TYPE.REVIEW_NEEDED]: "Flagged: review needed",
  [EVENT_TYPE.REVOKED]: "Revoked",
  [EVENT_TYPE.SUPERSEDED]: "Superseded",
  [EVENT_TYPE.REAUTHORIZED]: "Re-authorized",
  [EVENT_TYPE.DIRECTIVES_ADDED]: "Directives added at review",
});

/**
 * Status precedence for the settled "status-rank, then date-desc" ordering.
 * Lower number floats higher. (status-rank, then date-desc.)
 */
export const STATUS_RANK = Object.freeze({
  [DIRECTIVE_STATUS.ACTIVE]: 0,
  [DIRECTIVE_STATUS.PENDING_REVIEW]: 1,
  [DIRECTIVE_STATUS.UNCONFIRMED_AUTHORIZATION]: 2,
  [DIRECTIVE_STATUS.EXPIRED]: 3,
  [DIRECTIVE_STATUS.SUPERSEDED]: 4,
  [DIRECTIVE_STATUS.REVOKED]: 5,
});

/**
 * Sort a list of directive_record objects: status-rank ascending, then newest
 * first within each rank. Newest is decided by approved_at || created_at.
 */
/**
 * Stable lookup key for a coded directive.
 *
 * A code is only unique WITHIN its code system. Several catalog categories have
 * no SNOMED concept in the loaded release and carry ERRIFF-LOCAL codes (issue
 * #177), so keying on the code alone risks a local code colliding with a real
 * SNOMED concept id — silently deduping a row out of a result list, or enriching
 * a directive with another concept's display text.
 *
 * @param {string} [codeSystem] defaults to SNOMED for pre-code_system rows
 * @param {string|number} code
 */
export function codeKey(codeSystem, code) {
  return (codeSystem || "SNOMED") + "|" + String(code);
}

/** Human label for a code system — never hardcode "SNOMED CT" beside a code. */
export const CODE_SYSTEM_LABELS = Object.freeze({
  SNOMED: "SNOMED CT",
  "ERRIFF-LOCAL": "Erriff (local)",
});

export function codeSystemLabel(codeSystem) {
  const key = codeSystem || "SNOMED";
  return CODE_SYSTEM_LABELS[key] || key;
}

export function sortDirectiveRecords(records) {
  const list = Array.isArray(records) ? records.slice() : [];
  return list.sort((a, b) => {
    const ra = STATUS_RANK[a.status] ?? 99;
    const rb = STATUS_RANK[b.status] ?? 99;
    if (ra !== rb) return ra - rb;
    const da = new Date(a.approved_at || a.created_at || 0).getTime();
    const db = new Date(b.approved_at || b.created_at || 0).getTime();
    return db - da;
  });
}

// ---------------------------------------------------------------------------
// READ
// ---------------------------------------------------------------------------

/**
 * Fetch the org-scoped patient list for the Directives list page — every patient
 * in the operator's accountnumber that has >=1 directive_record, with summary
 * fields (record count, latest status, last-updated). Backs /directives.
 *
 * @param {{accountnumber:string}} params
 * @returns {Promise<Array>} patient summary rows
 */
export async function getDirectivePatients({ accountnumber, actorId }) {
  const content = {
    Directives: {
      API_Meth: "GetDirectivePatients",
      accountnumber,
      actor_id: actorId,
    },
  };
  const res = await axios.post(WEBHOOK, content, {
    headers: { "Content-Type": "application/json" },
  });
  // Expected shape: { status: 'Successful', data: [ ...patient summary ] }
  return res && res.data && Array.isArray(res.data.data) ? res.data.data : [];
}

/**
 * Server-side patient search for the "New Patient Record" entry point on
 * /directives. Matches the org's active registered patients by name (the keyon
 * endpoint enforces a 3-character minimum and a 50-row cap). Each row carries
 * `record_count` so the caller can route an already-recorded patient straight to
 * their detail page instead of re-running the wizard.
 *
 * @param {{accountnumber:string, query:string}} params
 * @returns {Promise<{patients:Array, capped:boolean}>}
 */
export async function searchDirectivePatients({ accountnumber, query, actorId }) {
  const content = {
    Directives: {
      API_Meth: "SearchDirectivePatients",
      accountnumber,
      query: query || "",
      actor_id: actorId,
    },
  };
  const res = await axios.post(WEBHOOK, content, {
    headers: { "Content-Type": "application/json" },
  });
  // Expected shape: { status: 'Successful', data: [ ...patient ], capped: bool }
  const patients = res && res.data && Array.isArray(res.data.data) ? res.data.data : [];
  const capped = !!(res && res.data && res.data.capped);
  return { patients, capped };
}

/**
 * Search the curated directive catalog by free text across
 * full_name / description / choice_label / code. v1 runs over the seeded
 * directive_catalog (the bounded, directive-eligible subset). Searching the full
 * SNOMED release is a deferred follow-up (see docs/follow-ups/).
 *
 * @param {string} query
 * @returns {Promise<Array>} directive_catalog rows matching the query
 */
export async function searchDirectives(query) {
  const content = {
    Directives: {
      API_Meth: "SearchDirectives",
      query: query || "",
    },
  };
  const res = await axios.post(WEBHOOK, content, {
    headers: { "Content-Type": "application/json" },
  });
  return res && res.data && Array.isArray(res.data.data) ? res.data.data : [];
}

/**
 * Search the FULL SNOMED release (the "search all of SNOMED" follow-up), backed
 * by the out-of-band `snomed_search` FULLTEXT table. Results are shaped so a pick
 * maps to a directive with catalog_id=null: { code, label, score }.
 *
 * Returns { results, available }. `available` is false only when the reference
 * table hasn't been built (the loader is opt-in) — the caller distinguishes
 * "no matches" (available, empty) from "SNOMED search unavailable" and falls
 * back to the curated catalog either way. The backend enforces a >=3-char floor.
 *
 * @param {string} query
 * @returns {Promise<{results:Array<{code:(string|number), label:string, score:number}>, available:boolean}>}
 */
export async function searchSnomed(query) {
  const content = {
    Directives: {
      API_Meth: "SearchSnomed",
      query: query || "",
    },
  };
  const res = await axios.post(WEBHOOK, content, {
    headers: { "Content-Type": "application/json" },
  });
  const results = res && res.data && Array.isArray(res.data.data) ? res.data.data : [];
  // Backend sends available:false when the table is absent; default to true.
  const available = !(res && res.data && res.data.available === false);
  return { results, available };
}

/**
 * Per-status directive_record counts for the operator's org (one GROUP BY
 * server-side), plus `attentionPatients` — DISTINCT patients with a record
 * awaiting sign-off (pending_review) or re-authorization (active past
 * reauth_due_at). Backs the navbar Directives badge. Statuses with zero
 * records are absent from the counts map — callers default to 0.
 *
 * @param {{accountnumber:string}} params
 * @returns {Promise<{counts:Object<string, number>, attentionPatients:number}>}
 *          e.g. { counts: { active: 2, pending_review: 4 }, attentionPatients: 3 }
 */
export async function getDirectiveCounts({ accountnumber, actorId }) {
  const content = {
    Directives: {
      API_Meth: "GetDirectiveCounts",
      accountnumber,
      actor_id: actorId,
    },
  };
  const res = await axios.post(WEBHOOK, content, {
    headers: { "Content-Type": "application/json" },
  });
  const counts =
    res && res.data && res.data.counts && typeof res.data.counts === "object"
      ? res.data.counts
      : {};
  const attentionPatients = Number(res && res.data && res.data.attention_patients) || 0;
  return { counts, attentionPatients };
}

/**
 * Fetch a patient's directive records (with nested directives + events).
 * @param {{accountnumber:string, patientPid:string|number}} params
 * @returns {Promise<Array>} directive_record rows
 */
export async function getDirectiveRecords({ accountnumber, patientPid, actorId }) {
  const content = {
    Directives: {
      API_Meth: "GetDirectiveRecords",
      accountnumber,
      patient_pid: patientPid,
      actor_id: actorId,
    },
  };
  const res = await axios.post(WEBHOOK, content, {
    headers: { "Content-Type": "application/json" },
  });
  // Expected shape: { status: 'Successful', data: [ ...directive_record ], patient: {…} }
  // A scope refusal comes back as { status: 'Error', message } and must NOT be
  // read as "this patient has no records" — the caller has to be able to tell
  // an empty chart from one it may not open.
  if (res && res.data && res.data.status && res.data.status !== "Successful") {
    return { records: [], patient: null, denied: true, message: res.data.message || "" };
  }
  const data = res && res.data && Array.isArray(res.data.data) ? res.data.data : [];
  const patient = res && res.data && res.data.patient ? res.data.patient : null;
  return { records: sortDirectiveRecords(data), patient, denied: false };
}

/**
 * Resolve directive_record.patient_pid (MySQL patients.pid) from session scope.
 * The Vuex session patient only carries LA_user fields (externId = psersonalID);
 * directives are keyed on patients.pid, which is a different column.
 */
export async function resolveDirectivePatientPid({ accountnumber, psersonalId }) {
  if (!accountnumber || psersonalId == null || psersonalId === '') {
    return '';
  }
  const content = {
    GetPatientInfo: {
      API_Meth: 'GetCurrentPatientData',
      accountNumber: accountnumber,
      pid: String(psersonalId),
    },
  };
  const res = await axios.post(WEBHOOK, content, {
    headers: { 'Content-Type': 'application/json' },
  });
  const row = res?.data?.patientInfo?.[0];
  const mysqlPid = row?.pid;
  return mysqlPid != null && mysqlPid !== '' ? String(mysqlPid) : '';
}

/**
 * Fetch the curated directive catalog (seeded ~11 rows). Drives Manual Entry.
 * @returns {Promise<Array>} directive_catalog rows
 */
export async function getDirectiveCatalog() {
  const content = {
    Directives: { API_Meth: "GetDirectiveCatalog" },
  };
  const res = await axios.post(WEBHOOK, content, {
    headers: { "Content-Type": "application/json" },
  });
  return res && res.data && Array.isArray(res.data.data) ? res.data.data : [];
}

// ---------------------------------------------------------------------------
// CREATE — Manual Entry
// ---------------------------------------------------------------------------

/**
 * Create a directive_record via the Manual Entry ingest path.
 * Requires >=1 selected catalog choice. Entry status = pending_review
 * (See the lifecycle rules on directive_record.status.)
 *
 * @param {object} payload
 * @param {string} payload.accountnumber
 * @param {string|number} payload.patientPid
 * @param {string} payload.createdById        submitter unique_ID
 * @param {string} payload.createdByName      submitter display name (snapshot)
 * @param {string} payload.relationshipToPatient  RELATIONSHIP_TO_PATIENT value
 * @param {string} [payload.relationshipOther]     free-text when relationship = other
 * @param {boolean} payload.isLegalSurrogate
 * @param {string} [payload.authorityType]         AUTHORITY_TYPE value (when surrogate)
 * @param {Array<{catalog_id:number, code:string, label:string}>} payload.directives
 */
export async function createManualDirectiveRecord(payload) {
  const content = {
    Directives: {
      API_Meth: "CreateDirectiveRecordManual",
      accountnumber: payload.accountnumber,
      patient_pid: payload.patientPid,
      ingest_path: INGEST_PATH.MANUAL,
      created_by_id: payload.createdById,
      created_by_name: payload.createdByName,
      relationship_to_patient: payload.relationshipToPatient,
      relationship_other: payload.relationshipOther || null,
      is_legal_surrogate: payload.isLegalSurrogate ? 1 : 0,
      authority_type: payload.isLegalSurrogate ? payload.authorityType || null : null,
      // Each becomes a `directive` row with provenance='manual', candidate='confirmed'.
      directives: (payload.directives || []).map((d) => ({
        catalog_id: d.catalog_id,
        code: d.code,
        code_system: d.code_system || "SNOMED",
        label: d.label,
        provenance: PROVENANCE.MANUAL,
        candidate_status: CANDIDATE_STATUS.CONFIRMED,
      })),
    },
  };
  const res = await axios.post(WEBHOOK, content, {
    headers: { "Content-Type": "application/json" },
  });
  return res.data;
}

// ---------------------------------------------------------------------------
// CREATE — Upload
// ---------------------------------------------------------------------------

/**
 * Create a directive_record via the Upload ingest path. The document is the
 * source of truth; coded directives are optional (0+). Entry status:
 *   document_authorized = true  -> pending_review
 *   document_authorized = false -> unconfirmed_authorization
 * Multipart; keyon stores the document and returns only `status` and `record_id`.
 *
 * @param {object} payload — same attestation fields as manual, plus:
 * @param {File} payload.file              PDF or camera image
 * @param {boolean} payload.documentAuthorized   the "fully authorized/notarized?" answer
 * @param {string} [payload.formLabel]     origin label e.g. 'MOLST/POLST'
 */
export async function createUploadDirectiveRecord(payload) {
  const fd = new FormData();
  fd.append("API_Meth", "CreateDirectiveRecordUpload");
  fd.append("accountnumber", payload.accountnumber);
  fd.append("patient_pid", String(payload.patientPid));
  fd.append("ingest_path", INGEST_PATH.UPLOAD);
  fd.append("created_by_id", payload.createdById);
  fd.append("created_by_name", payload.createdByName);
  fd.append("relationship_to_patient", payload.relationshipToPatient);
  fd.append("relationship_other", payload.relationshipOther || "");
  fd.append("is_legal_surrogate", payload.isLegalSurrogate ? "1" : "0");
  fd.append(
    "authority_type",
    payload.isLegalSurrogate ? payload.authorityType || "" : ""
  );
  fd.append("document_authorized", payload.documentAuthorized ? "1" : "0");
  fd.append("form_label", payload.formLabel || "");
  fd.append("file", payload.file);
  const res = await axios.post(WEBHOOK, fd, {
    // NOTE: do not hand-set Content-Type for FormData; the browser sets the
    // multipart boundary, and keyon's cross-site guard accepts only JSON or
    // multipart.
    headers: {},
  });
  return res.data;
}

// ---------------------------------------------------------------------------
// LIFECYCLE actions
// ---------------------------------------------------------------------------

/**
 * Clinician sign-off: pending_review -> active. Server enforces BOTH gates —
 * directive_approve_reauthorize privilege AND verified NPI possession. The UI
 * pre-gates for UX, but the server is authoritative.
 *
 * @param {object} payload
 * @param {string|number} payload.recordId
 * @param {string} payload.actorId      signer unique_ID
 * @param {string} payload.actorName    signer display name (snapshot)
 * @param {string} payload.actorNpi     signer NPI (snapshot)
 * @param {Array} [payload.assignedDirectives]  codes assigned at sign-off when an
 *        Upload record extracted 0 directives (provenance='signoff'). Each:
 *        {catalog_id, code, label}
 */
/**
 * keyon returns its failures as HTTP 200 with `{status:"Error", message}`, so a
 * bare axios call resolves happily on a rejected write. Every lifecycle mutation
 * routes through this so a refusal (missing privilege, wrong status, no
 * credential) reaches the caller as a thrown Error carrying the server's own
 * message, instead of being reported to the user as success.
 *
 * @param {object} data   res.data from the webhook
 * @param {string} fallback message used when the server sent none
 */
function assertWebhookOk(data, fallback) {
  const status = String((data && data.status) || "");
  if (status.toLowerCase().startsWith("success")) return data;
  const err = new Error((data && data.message) || fallback);
  err.serverMessage = (data && data.message) || "";
  err.code = data && typeof data.code === "string" ? data.code : null;
  throw err;
}

/**
 * The lifecycle writes that keyon can refuse with a real HTTP status: a
 * pending upload with no document is a 409 `document_removed`
 * (keyon SqlClass SignDirectiveRecord / ConfirmDirectiveAuthorization; a
 * removal through DeleteDirectiveDocument closes the submission instead). axios
 * rejects on it, so keyon's body is read from the rejection and goes through
 * assertWebhookOk like the 200 refusals; a failure with no keyon body is
 * rethrown as it was.
 */
async function postLifecycle(content) {
  try {
    const res = await axios.post(WEBHOOK, content, {
      headers: { "Content-Type": "application/json" },
    });
    return res && res.data;
  } catch (err) {
    const data = err && err.response ? err.response.data : null;
    if (data && typeof data === "object" && typeof data.status === "string") return data;
    throw err;
  }
}

/**
 * The signing credential keyon holds for a user (NPI / professional license).
 *
 * A clinician's credential can live in EITHER store: keyon `account_user`
 * .proffesionalLicense (Add User / verify-provider onboarding) or pacmny-be's
 * Postgres LA_user.ZV_user_npi (direct /sessionless/register). The session
 * payload only ever carries the Postgres one, so the UI asks keyon for its copy
 * before deciding whether the signer holds a credential at all.
 *
 * @returns {Promise<string>} the licence/NPI, or "" when keyon holds none
 */
export async function getDirectiveSignerCredential({ accountnumber, uniqueId }) {
  if (!accountnumber || !uniqueId) return "";
  const content = {
    Directives: {
      API_Meth: "GetDirectiveSignerCredential",
      accountnumber: String(accountnumber),
      unique_ID: String(uniqueId),
    },
  };
  const res = await axios.post(WEBHOOK, content, {
    headers: { "Content-Type": "application/json" },
  });
  const data = res && res.data;
  const ok = String((data && data.status) || "").toLowerCase().startsWith("success");
  return ok && data.data ? String(data.data.professional_license || "").trim() : "";
}

/**
 * Confirm the authorization of a document uploaded with "is this fully
 * authorized / notarized?" answered No: unconfirmed_authorization ->
 * pending_review, so the record can reach clinician sign-off. Writes an
 * `authorization_confirmed` event. Requires the upload/modify privilege (53).
 *
 * @param {object} payload
 * @param {string|number} payload.recordId
 * @param {string} payload.actorId
 * @param {string} payload.actorName
 * @param {string} [payload.actorNpi]
 */
export async function confirmDirectiveAuthorization(payload) {
  const content = {
    Directives: {
      API_Meth: "ConfirmDirectiveAuthorization",
      directive_record_id: payload.recordId,
      actor_id: payload.actorId,
      actor_name: payload.actorName,
      actor_npi: payload.actorNpi || "",
    },
  };
  return assertWebhookOk(await postLifecycle(content), "Could not confirm this document's authorization.");
}

/**
 * Add coded directives to an UPLOADED record during review, before sign-off.
 *
 * The signer's assign-at-sign-off path (signDirectiveRecord's assignedDirectives)
 * still exists and is unchanged; this is the same write decoupled from
 * activation, so a reviewer holding upload/modify (53) — who may have no NPI and
 * so can never sign — can code a document the upload extracted nothing from.
 * Server refuses anything but an upload record in pending_review /
 * unconfirmed_authorization, and silently skips codes already on the record.
 *
 * @param {object} payload
 * @param {string|number} payload.recordId
 * @param {string} payload.actorId
 * @param {string} payload.actorName
 * @param {string} [payload.actorNpi]
 * @param {Array} payload.directives  {catalog_id, code, code_system, label}
 */
export async function addDirectivesToRecord(payload) {
  const content = {
    Directives: {
      API_Meth: "AddDirectivesToRecord",
      directive_record_id: payload.recordId,
      actor_id: payload.actorId,
      actor_name: payload.actorName,
      actor_npi: payload.actorNpi || "",
      directives: (payload.directives || []).map((d) => ({
        catalog_id: d.catalog_id,
        code: d.code,
        code_system: d.code_system || "SNOMED",
        label: d.label,
      })),
    },
  };
  const res = await axios.post(WEBHOOK, content, {
    headers: { "Content-Type": "application/json" },
  });
  return assertWebhookOk(res.data, "Could not add directives to this record.");
}

export async function signDirectiveRecord(payload) {
  const content = {
    Directives: {
      API_Meth: "SignDirectiveRecord",
      directive_record_id: payload.recordId,
      actor_id: payload.actorId,
      actor_name: payload.actorName,
      actor_npi: payload.actorNpi,
      assigned_directives: (payload.assignedDirectives || []).map((d) => ({
        catalog_id: d.catalog_id,
        code: d.code,
        code_system: d.code_system || "SNOMED",
        label: d.label,
        provenance: PROVENANCE.SIGNOFF,
        candidate_status: CANDIDATE_STATUS.CONFIRMED,
      })),
    },
  };
  return assertWebhookOk(await postLifecycle(content), "Could not sign this directive.");
}

/**
 * Mark a record as reviewed (writes a 'reviewed' directive_event + updates the
 * last_reviewed_* snapshot columns).
 */
export async function markDirectiveReviewed(payload) {
  const content = {
    Directives: {
      API_Meth: "MarkDirectiveReviewed",
      directive_record_id: payload.recordId,
      actor_id: payload.actorId,
      actor_name: payload.actorName,
    },
  };
  const res = await axios.post(WEBHOOK, content, {
    headers: { "Content-Type": "application/json" },
  });
  return res.data;
}

/**
 * Flag a record as needing review (writes a 'review_needed' directive_event with
 * a short note). Does NOT change status — it is a soft flag (patient-safety).
 */
export async function flagDirectiveReviewNeeded(payload) {
  const content = {
    Directives: {
      API_Meth: "FlagDirectiveReviewNeeded",
      directive_record_id: payload.recordId,
      actor_id: payload.actorId,
      actor_name: payload.actorName,
      note: payload.note || "",
    },
  };
  const res = await axios.post(WEBHOOK, content, {
    headers: { "Content-Type": "application/json" },
  });
  return res.data;
}

/**
 * Re-authorize an active record: resets the 90-day re-auth window and writes a
 * 'reauthorized' event. Server requires the approve/re-authorize privilege + NPI
 * (same gate as sign-off) and that the record is currently active.
 *
 * @param {object} payload
 * @param {string|number} payload.recordId
 * @param {string} payload.actorId
 * @param {string} payload.actorName
 * @param {string} payload.actorNpi
 */
export async function reauthorizeDirectiveRecord(payload) {
  const content = {
    Directives: {
      API_Meth: "ReauthorizeDirectiveRecord",
      directive_record_id: payload.recordId,
      actor_id: payload.actorId,
      actor_name: payload.actorName,
      actor_npi: payload.actorNpi,
    },
  };
  const res = await axios.post(WEBHOOK, content, {
    headers: { "Content-Type": "application/json" },
  });
  return res.data;
}

/**
 * Soft-delete a mistaken directive record. The server reuses the 'revoked' status
 * and writes a 'revoked' event — NO hard delete (HIPAA: the record + history are
 * retained). Allowed only on pending_review / unconfirmed_authorization records.
 *
 * @param {object} payload
 * @param {string|number} payload.recordId
 * @param {string} payload.actorId
 * @param {string} payload.actorName
 * @param {string} [payload.actorNpi]
 */
export async function revokeDirectiveRecord(payload) {
  const content = {
    Directives: {
      API_Meth: "RevokeDirectiveRecord",
      directive_record_id: payload.recordId,
      actor_id: payload.actorId,
      actor_name: payload.actorName,
      actor_npi: payload.actorNpi || "",
    },
  };
  const res = await axios.post(WEBHOOK, content, {
    headers: { "Content-Type": "application/json" },
  });
  return res.data;
}

// ===========================================================================
// MOCK DATA — clearly labeled, used to render the page for review while the
// backend is unimplemented. Remove once the real endpoints land.
// ===========================================================================

/**
 * Mirrors the seeded directive_catalog (migration INSERT). full_name + description
 * are copied verbatim from the migration seed so the rich, safety-oriented
 * selection UI shows the same code + full name + description as the DB.
 */
export function getDirectiveCatalogMock() {
  return [
    { id: 1, category_code: "dnr", category_label: "Do Not Resuscitate (DNR)", choice_label: "Do Not Resuscitate (DNR)", full_name: "Do not resuscitate", description: "Do not attempt cardiopulmonary resuscitation (CPR) if the heart or breathing stops.", code: "304253006", code_system: "SNOMED", sort_order: 10 },
    { id: 2, category_code: "dni", category_label: "Do Not Intubate (DNI)", choice_label: "Do Not Intubate (DNI)", full_name: "Do not intubate", description: "Do not insert a breathing tube or start mechanical ventilation via intubation.", code: "DNI", code_system: "ERRIFF-LOCAL", sort_order: 20 },
    { id: 3, category_code: "dnr_dni", category_label: "DNR/DNI (combined)", choice_label: "DNR/DNI (combined)", full_name: "Do not resuscitate and do not intubate", description: "No CPR and no intubation.", code: "DNR-DNI", code_system: "ERRIFF-LOCAL", sort_order: 30 },
    { id: 4, category_code: "full_code", category_label: "Full Code", choice_label: "Full Code", full_name: "Full resuscitation (full code)", description: "Attempt all resuscitative measures, including CPR and intubation.", code: "FULL-CODE", code_system: "ERRIFF-LOCAL", sort_order: 40 },
    { id: 5, category_code: "comfort_measures", category_label: "Comfort Measures Only / Allow Natural Death", choice_label: "Comfort Measures Only (CMO)", full_name: "Comfort measures only", description: "Care focused on comfort; no life-prolonging interventions.", code: "CMO", code_system: "ERRIFF-LOCAL", sort_order: 50 },
    { id: 6, category_code: "comfort_measures", category_label: "Comfort Measures Only / Allow Natural Death", choice_label: "Allow Natural Death (AND)", full_name: "Allow natural death", description: "Permit the natural dying process without resuscitative intervention.", code: "AND", code_system: "ERRIFF-LOCAL", sort_order: 51 },
    { id: 7, category_code: "dnh", category_label: "Do Not Hospitalize (DNH)", choice_label: "Do Not Hospitalize (DNH)", full_name: "Do not hospitalize", description: "Do not transfer to hospital; treat within the current care setting.", code: "DNH", code_system: "ERRIFF-LOCAL", sort_order: 60 },
    { id: 8, category_code: "tube_feeding", category_label: "Tube Feeding Preferences", choice_label: "No artificial nutrition", full_name: "No artificial nutrition", description: "Do not provide nutrition via feeding tube or other artificial means.", code: "NO-ARTIFICIAL-NUTRITION", code_system: "ERRIFF-LOCAL", sort_order: 70 },
    { id: 9, category_code: "tube_feeding", category_label: "Tube Feeding Preferences", choice_label: "Artificial nutrition acceptable", full_name: "Artificial nutrition acceptable", description: "Nutrition via feeding tube or other artificial means is acceptable.", code: "ARTIFICIAL-NUTRITION-OK", code_system: "ERRIFF-LOCAL", sort_order: 71 },
    { id: 10, category_code: "ventilation", category_label: "Ventilation Preferences", choice_label: "No mechanical ventilation", full_name: "No mechanical ventilation", description: "Do not provide mechanical ventilator support.", code: "NO-MECHANICAL-VENTILATION", code_system: "ERRIFF-LOCAL", sort_order: 80 },
    { id: 11, category_code: "ventilation", category_label: "Ventilation Preferences", choice_label: "Mechanical ventilation acceptable", full_name: "Mechanical ventilation acceptable", description: "Mechanical ventilator support is acceptable.", code: "MECHANICAL-VENTILATION-OK", code_system: "ERRIFF-LOCAL", sort_order: 81 },
  ];
}

/**
 * v1 SearchDirectives mock — filters the curated catalog by a free-text query
 * across full_name / description / choice_label / code. Mirrors what the
 * backend SearchDirectives endpoint is expected to return.
 * @param {string} query
 * @returns {Array} matching directive_catalog rows
 */
export function searchDirectivesMock(query) {
  const q = String(query || "").trim().toLowerCase();
  const catalog = getDirectiveCatalogMock();
  if (!q) return catalog;
  return catalog.filter((c) => {
    const haystack = [c.full_name, c.description, c.choice_label, c.code]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return haystack.includes(q);
  });
}

/**
 * Org-scoped patient summary mock for the /directives list page. Each row is a
 * patient with >=1 directive_record and the summary fields the list shows.
 * patient_pid + accountnumber mirror directive_record's patient scope.
 */
export function getDirectivePatientsMock() {
  return [
    {
      patient_pid: 1,
      accountnumber: "MOCK-ACCT",
      patient_name: "John Patient",
      mrn: "MRN 4471-2208",
      dob: "1948-03-12",
      record_count: 4,
      latest_status: "active",
      current_status: "active",
      last_updated_at: "2026-05-20T09:00:00",
    },
    {
      patient_pid: 2,
      accountnumber: "MOCK-ACCT",
      patient_name: "Eleanor Vance",
      mrn: "MRN 3390-1185",
      dob: "1939-11-02",
      record_count: 1,
      latest_status: "pending_review",
      current_status: "pending_review",
      last_updated_at: "2026-06-14T10:15:00",
    },
    {
      patient_pid: 3,
      accountnumber: "MOCK-ACCT",
      patient_name: "Marcus Webb",
      mrn: "MRN 5521-7740",
      dob: "1955-07-28",
      record_count: 2,
      latest_status: "unconfirmed_authorization",
      current_status: "active",
      last_updated_at: "2026-06-12T14:00:00",
    },
    {
      patient_pid: 4,
      accountnumber: "MOCK-ACCT",
      patient_name: "Sofia Ramirez",
      mrn: "MRN 6612-3008",
      dob: "1962-01-19",
      record_count: 3,
      latest_status: "revoked",
      current_status: "revoked",
      last_updated_at: "2026-03-30T16:40:00",
    },
  ];
}

/**
 * A representative set of directive_record rows (with nested directives + events)
 * covering several statuses + both ingest paths, so the page renders for review.
 */
export function getDirectiveRecordsMock() {
  return [
    {
      id: 101,
      accountnumber: "MOCK-ACCT",
      patient_pid: 1,
      ingest_path: "upload",
      status: "active",
      document_file_uid: "a1b2c3d4e5f60718293a4b5c6d7e8f90",
      has_document: 1,
      document_name: "molst-2026.pdf",
      document_content_type: "application/pdf",
      document_authorized: 1,
      form_label: "MOLST/POLST",
      created_by_id: "u-200",
      created_by_name: "Maria Gomez",
      relationship_to_patient: "child",
      relationship_other: null,
      is_legal_surrogate: 1,
      authority_type: "health_care_proxy",
      approved_at: "2026-04-02T15:10:00",
      approved_by_id: "u-900",
      approved_by_name: "Dr. Alan Pierce",
      approved_by_npi: "1457392011",
      last_reviewed_at: "2026-05-20T09:00:00",
      last_reviewed_by_id: "u-901",
      last_reviewed_by_name: "RN Tanya Brooks",
      reauth_due_at: "2026-07-01T15:10:00",
      superseded_by_id: null,
      revoked_at: null,
      created_at: "2026-04-01T11:00:00",
      directives: [
        { id: 1, code: "304253006", label: "Do Not Resuscitate (DNR)", provenance: "extracted", candidate_status: "confirmed" },
        { id: 2, code: "410211008", label: "Do Not Intubate (DNI)", provenance: "signoff", candidate_status: "confirmed" },
      ],
      events: [
        { id: 1, event_type: "created", actor_name: "Maria Gomez", actor_npi: null, note: null, occurred_at: "2026-04-01T11:00:00" },
        { id: 2, event_type: "signed", actor_name: "Dr. Alan Pierce", actor_npi: "1457392011", note: null, occurred_at: "2026-04-02T15:10:00" },
        { id: 3, event_type: "reviewed", actor_name: "RN Tanya Brooks", actor_npi: null, note: null, occurred_at: "2026-05-20T09:00:00" },
      ],
    },
    {
      id: 102,
      accountnumber: "MOCK-ACCT",
      patient_pid: 1,
      ingest_path: "manual",
      status: "pending_review",
      document_file_uid: null,
      has_document: 0,
      document_name: null,
      document_content_type: null,
      document_authorized: null,
      form_label: null,
      created_by_id: "u-1",
      created_by_name: "John Patient",
      relationship_to_patient: "self",
      relationship_other: null,
      is_legal_surrogate: 0,
      authority_type: null,
      approved_at: null,
      approved_by_id: null,
      approved_by_name: null,
      approved_by_npi: null,
      last_reviewed_at: null,
      last_reviewed_by_id: null,
      last_reviewed_by_name: null,
      reauth_due_at: null,
      superseded_by_id: null,
      revoked_at: null,
      created_at: "2026-06-10T08:30:00",
      directives: [
        { id: 4, code: "304252001", label: "Comfort Measures Only (CMO)", provenance: "manual", candidate_status: "confirmed" },
        { id: 5, code: "304254000", label: "Do Not Hospitalize (DNH)", provenance: "manual", candidate_status: "confirmed" },
      ],
      events: [
        { id: 4, event_type: "created", actor_name: "John Patient", actor_npi: null, note: null, occurred_at: "2026-06-10T08:30:00" },
      ],
    },
    {
      id: 103,
      accountnumber: "MOCK-ACCT",
      patient_pid: 1,
      ingest_path: "upload",
      status: "unconfirmed_authorization",
      document_file_uid: "b1b2c3d4e5f60718293a4b5c6d7e8f90",
      has_document: 1,
      document_name: "scanned-form.jpg",
      document_content_type: "image/jpeg",
      document_authorized: 0,
      form_label: "Living Will",
      created_by_id: "u-201",
      created_by_name: "Robert Friend",
      relationship_to_patient: "friend",
      relationship_other: null,
      is_legal_surrogate: 0,
      authority_type: null,
      approved_at: null,
      approved_by_id: null,
      approved_by_name: null,
      approved_by_npi: null,
      last_reviewed_at: null,
      last_reviewed_by_id: null,
      last_reviewed_by_name: null,
      reauth_due_at: null,
      superseded_by_id: null,
      revoked_at: null,
      created_at: "2026-06-12T14:00:00",
      directives: [],
      events: [
        { id: 5, event_type: "created", actor_name: "Robert Friend", actor_npi: null, note: null, occurred_at: "2026-06-12T14:00:00" },
      ],
    },
    {
      id: 100,
      accountnumber: "MOCK-ACCT",
      patient_pid: 1,
      ingest_path: "upload",
      status: "superseded",
      document_file_uid: "c1b2c3d4e5f60718293a4b5c6d7e8f90",
      has_document: 1,
      document_name: "molst-2025.pdf",
      document_content_type: "application/pdf",
      document_authorized: 1,
      form_label: "MOLST/POLST",
      created_by_id: "u-200",
      created_by_name: "Maria Gomez",
      relationship_to_patient: "child",
      relationship_other: null,
      is_legal_surrogate: 1,
      authority_type: "health_care_proxy",
      approved_at: "2025-09-15T10:00:00",
      approved_by_id: "u-900",
      approved_by_name: "Dr. Alan Pierce",
      approved_by_npi: "1457392011",
      last_reviewed_at: null,
      last_reviewed_by_id: null,
      last_reviewed_by_name: null,
      reauth_due_at: null,
      superseded_by_id: 101,
      revoked_at: null,
      created_at: "2025-09-14T10:00:00",
      directives: [
        { id: 6, code: "304253006", label: "Do Not Resuscitate (DNR)", provenance: "manual", candidate_status: "confirmed" },
      ],
      events: [
        { id: 6, event_type: "created", actor_name: "Maria Gomez", actor_npi: null, note: null, occurred_at: "2025-09-14T10:00:00" },
        { id: 7, event_type: "signed", actor_name: "Dr. Alan Pierce", actor_npi: "1457392011", note: null, occurred_at: "2025-09-15T10:00:00" },
        { id: 8, event_type: "superseded", actor_name: "Dr. Alan Pierce", actor_npi: "1457392011", note: null, occurred_at: "2026-04-02T15:10:00" },
      ],
    },
  ];
}
